import { LogoBox, AppMenu, HeaderPartnerLoginDropdown } from '@/components';
import { mainPublicServiceNav } from '@/assets/data';
import { useToggle } from '@/hooks';
import clsx from 'clsx';
import { Container, Collapse } from 'react-bootstrap';
import Link from 'next/link';
import { usePathname } from 'next/navigation';
import { useEffect, useRef, useState } from 'react';
import { FiAtSign } from 'react-icons/fi';
import StickySearchBar from '@/components/StickySearchBar';

const TopNavBar = () => {
  const pathname = usePathname();
  const { isOpen: mobileOpen, toggle: mobileToggle, hide: hideMobile } = useToggle(false);
  const [stickySearchEligible, setStickySearchEligible] = useState(false);
  const menuContainerRef = useRef(null);
  const menuButtonRef = useRef(null);

  const isPartnerExcursionsListing = /^\/partner\/[^/]+\/excursions\/?$/.test(pathname);
  const isExcursionDetailPage =
    pathname.startsWith('/excursions/')
    || /^\/partner\/[^/]+\/excursions\/[^/]+/.test(pathname);
  const isServicePage =
    pathname.startsWith('/hotels') ||
    pathname.startsWith('/excursions') ||
    isPartnerExcursionsListing;
  const shouldShowSearchBar = isServicePage && !isExcursionDetailPage;

  useEffect(() => {
    const onDoc = (e) => {
      if (!mobileOpen) return;
      const t = e.target;
      if (menuContainerRef.current?.contains(t) || menuButtonRef.current?.contains(t)) return;
      hideMobile();
    };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, [mobileOpen, hideMobile]);

  useEffect(() => {
    if (!shouldShowSearchBar) {
      setStickySearchEligible(false);
      return;
    }

    const maybeEnableStickySearch = () => {
      if (window.scrollY > 120) {
        setStickySearchEligible(true);
      }
    };

    maybeEnableStickySearch();
    if (!stickySearchEligible) {
      window.addEventListener('scroll', maybeEnableStickySearch, { passive: true });
    }

    return () => {
      window.removeEventListener('scroll', maybeEnableStickySearch);
    };
  }, [shouldShowSearchBar, stickySearchEligible, pathname]);

  return (
    <>
      <header className="site-header-gyg">
        <div className="site-header-gyg__primary border-bottom border-light">
          <Container>
            <div className="d-flex align-items-center site-header-gyg__primary-inner gap-2 gap-md-3">
              <button
                ref={menuButtonRef}
                type="button"
                className="site-header-gyg__toggler d-lg-none"
                aria-controls="gygNavCollapse"
                aria-expanded={mobileOpen}
                aria-label="Toggle navigation"
                onClick={mobileToggle}
              >
                <span className="navbar-toggler-animation">
                  <span />
                  <span />
                  <span />
                </span>
              </button>

              <div className="flex-shrink-0">
                <LogoBox />
              </div>

              <div className="flex-grow-1 d-none d-lg-flex justify-content-center px-2">
                <div className="site-header-gyg__primary-nav d-flex align-items-center flex-wrap gap-1">
                  {mainPublicServiceNav.map((item) => {
                    const active =
                      item.key === 'dmc-services'
                        ? pathname === '/services' || pathname.startsWith('/services/')
                        : pathname === item.url || pathname.startsWith(`${item.url}/`);
                    return (
                      <Link
                        key={item.key}
                        href={item.url}
                        className={clsx('site-header-gyg__service-link', 'site-header-gyg__service-link--primary', active && 'active')}
                      >
                        {item.label}
                      </Link>
                    );
                  })}
                </div>
              </div>

              <div className="ms-auto d-flex align-items-center site-header-gyg__actions flex-shrink-0">
                <Link
                  href="/contact"
                  className={clsx(
                    'site-header-gyg__action text-decoration-none text-body d-none d-md-flex flex-column align-items-center gap-1 p-1 border-0 bg-transparent shadow-none',
                    pathname === '/contact' && 'active'
                  )}
                >
                  <FiAtSign className="site-header-gyg__action-icon" />
                  <span className="site-header-gyg__action-label">Contact</span>
                </Link>

                <HeaderPartnerLoginDropdown />
              </div>
            </div>

          </Container>
        </div>

        <Collapse in={mobileOpen}>
          <div id="gygNavCollapse" className="d-lg-none border-top bg-white">
            <Container className="py-3" ref={menuContainerRef}>
              <div className="d-flex flex-wrap gap-2 mb-3">
                {mainPublicServiceNav.map((item) => (
                  <Link
                    key={item.key}
                    href={item.url}
                    className="btn btn-sm btn-outline-secondary rounded-pill"
                    onClick={hideMobile}
                  >
                    {item.label}
                  </Link>
                ))}
                <Link href="/contact" className="btn btn-sm btn-outline-secondary rounded-pill" onClick={hideMobile}>
                  Contact
                </Link>
              </div>
              <AppMenu embedded />
            </Container>
          </div>
        </Collapse>
      </header>
      {shouldShowSearchBar && stickySearchEligible ? <StickySearchBar /> : null}
    </>
  );
};

export default TopNavBar;
